/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 */
import Echo from "laravel-echo";
import Pusher from "pusher-js";

import { useStores } from "./stores";

/**
 * A composable function that provides real-time event broadcasting capabilities using Laravel Echo and Pusher
 * @module useBroadcast
 * @returns {Object} An object containing init and subscribe methods
 */
export const useBroadcast = () => {
    /** Reference to Alpine store. */
    const stores = useStores();
    /**
     * Event names configuration object
     * @type {Object}
     */
    const events = {
        PlayerAccountUpdatedBalance: ".PlayerAccountUpdatedBalance",
        ApiCasinoGameBetFailed: ".ApiCasinoGameBetFailed",
        SubscriptionCount: "pusher:subscription_count",
    };
    /**
     * Root URL where to connect ECHO service.
     */
    const rootURL =
        window.appContext.Brand.url ??
        window.location.origin ??
        import.meta.env.VITE_APP_URL;
    /**
     * Initializes the Echo instance and sets up WebSocket connections
     * Configures Pusher with environment variables and establishes connection
     * @function
     * @returns {void}
     */
    const init = (channels = ["private", "presence"]) => {
        if (window.appContext?.Player?.uuid) {
            try {
                window.Pusher = Pusher;

                if (import.meta.env.VITE_REVERB_APP_KEY) {
                    // Use Reverb if VITE_REVERB_APP_KEY is defined
                    window.Echo = new Echo({
                        authEndpoint: `${rootURL}/broadcasting/auth`,
                        broadcaster: "reverb",
                        key: import.meta.env.VITE_REVERB_APP_KEY,
                        wsHost: import.meta.env.VITE_REVERB_HOST,
                        wssPort: import.meta.env.VITE_REVERB_PORT ?? 443,
                        forceTLS: true,
                        enabledTransports: ["ws", "wss"],
                    });
                } else {
                    // Use Pusher if VITE_REVERB_APP_KEY is not defined
                    window.Echo = new Echo({
                        authEndpoint: `${rootURL}/broadcasting/auth`,
                        broadcaster: "pusher",
                        key: import.meta.env.VITE_PUSHER_APP_KEY,
                        cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
                        wsHost: import.meta.env.VITE_PUSHER_HOST
                            ? import.meta.env.VITE_PUSHER_HOST
                            : `ws-${
                                  import.meta.env.VITE_PUSHER_APP_CLUSTER
                              }.pusher.com`,
                        wsPort: import.meta.env.VITE_PUSHER_PORT ?? 80,
                        wssPort: import.meta.env.VITE_PUSHER_PORT ?? 443,
                        forceTLS:
                            (import.meta.env.VITE_PUSHER_SCHEME ?? "https") ===
                            "https",
                        enabledTransports: ["ws", "wss"],
                    });
                }

                channels.forEach((channel) => subscribe(channel));
            } catch (error) {
                console.error("Error initializing Echo:", error);
            }
        }
    };
    /**
     * Handles subscription count for the current player.
     * Only one active session is allowed to play game.
     * @private
     * @returns {void}
     */
    const _trackSession = () => {
        // Temporarily disabled: pusher:subscription_count over-counts stale sockets, falsely flagging multiple tabs.
        // channels.sessions.instance?.on("pusher:subscription_count", (e) => {
        //     stores.getStore("sessions").multiple = e.subscription_count > 1;
        // });
    };
    /**
     * Handles bet failure error.
     * @private
     * @returns {void}
     */
    const _trackApiCasinoBetFailed = () => {
        channels.private.instance?.listen(
            events.ApiCasinoGameBetFailed,
            (e) => {
                const store = stores.getStore("sessions");
                switch (e.error_code) {
                    case "INSUFFICIENT_BALANCE":
                        store.insufficientBalance = true;
                        break;
                    case "BET_LIMIT_EXCEEDED":
                        store.betLimitExceeded = true;
                        break;
                    default:
                        console.error("Unhandled private event error");
                        console.log(e);
                }
            },
        );
    };

    /**
     * Handles balance updates from the server
     * Updates balance stores and manages bonus information
     * @private
     * @returns {void}
     */
    const _trackBalance = () => {
        channels.private.instance?.listen(
            events.PlayerAccountUpdatedBalance,
            (e) => {
                // Update all balances stores.
                stores.balanceNames.forEach((b) => {
                    stores.getStore(b).value = e[b];
                });
                // grab bonus store reference.
                const bonus = stores.getStore("active_bonus");

                // a bonus is sent in the event:
                if ("active_bonus" in e && e.active_bonus) {
                    // update all bonuses values.
                    stores.bonusNames.forEach((b) => {
                        bonus[b].value = e["active_bonus"][b];
                    });
                    // update other values: title, date.
                    bonus.title = e.active_bonus.title;
                    bonus.expired_at.value = e.active_bonus.expired_at
                        ? new Date(e.active_bonus.expired_at)
                        : null;
                } else {
                    // no bonus received: clear the store to pristine state.
                    bonus.clear();
                }
            },
        );
    };
    /**
     * Creates and configures a private channel instance for a player
     * @private
     * @returns {Object} The configured private channel instance
     */
    const _getPrivateChannelInstance = () => {
        const instance = window.Echo.private(
            channels.private.key.replace(
                "{uuid}",
                window.appContext.Player.uuid,
            ),
        );
        instance
            .subscribed(() => {
                _trackBalance();
                _trackApiCasinoBetFailed();
            })
            .error((error) => {
                console.warn("Error subscribing to private channel:", error);
            });
        return instance;
    };
    /**
     * Creates and configures a session channel instance for a player
     * Session channel is a private "sub" channel.
     * @private
     * @returns {Object} The configured presence channel instance
     */
    const _getSessionChannelInstance = () => {
        const instance = window.Echo.private(
            channels.sessions.key.replace(
                "{uuid}",
                window.appContext.Player.uuid,
            ),
        );
        instance
            .subscribed(() => {
                _trackSession();
            })
            .error((error) => {
                console.warn("Error subscribing to session channel:", error);
            });

        return instance;
    };
    /**
     * Creates and configures a presence channel instance for a player
     * @private
     * @returns {Object} The configured presence channel instance
     */
    const _getPresenceChannelInstance = () => {
        const instance = window.Echo.join(
            channels.presence.key.replace(
                "{uuid}",
                window.appContext.Player.uuid,
            ),
        );
        instance.error((error) => {
            console.warn("Error subscribing to presence channel:", error);
        });
        return instance;
    };
    /**
     * Configuration object for different channel types
     * @type {Object}
     */

    const channels = {
        private: {
            instance: null,
            key: "playerAccount.{uuid}",
            handler: _getPrivateChannelInstance,
        },
        sessions: {
            instance: null,
            key: "playerAccount.{uuid}.sessions",
            handler: _getSessionChannelInstance,
        },
        presence: {
            instance: null,
            key: "playerAccount.{uuid}",
            handler: _getPresenceChannelInstance,
        },
    };
    /**
     * Subscribes to a specified channel type if player UUID is available
     * @param {string} channel - The channel type to subscribe to
     * @returns {Object|undefined} The channel instance if successfully subscribed
     */
    const subscribe = (channel) => {
        if (
            window.appContext?.Player?.uuid &&
            Object.hasOwn(channels, channel)
        ) {
            if (!channels[channel].instance) {
                channels[channel].instance = channels[channel].handler();
            }
            return channels[channel].instance;
        }
    };

    return { init, subscribe };
};
